import React, { createContext, useState, useEffect, useCallback, useRef } from "react";
import { useAuth0 } from '@auth0/auth0-react';
import { clearLocalStorageItems, isTokenExpired, logTokenStatus } from '../utils/authUtils';
import { createApiCallWithTokenCheck } from '../utils/apiUtils';
import { config } from "../config";

export const AppContext = createContext();


export default function Context({ children }) {
  const {
    isLoading,
    getIdTokenClaims,
    logout,
    isAuthenticated,
    loginWithRedirect,
    user,
  } = useAuth0();

  const [isOpen, setIsOpen] = useState(true);
  const [isLoad, setIsLoad] = useState(false);
  const [data, setData] = useState([]);
  const [filter, setFilter] = useState("");
  const [channelData, setChannelData] = useState([]);
  const [selectedChannel, setSelectedChannel] = useState("");
  const [selectedType, setSelectedType] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const ACCOUNT_CACHE_KEY = "admin_app_account_cache";
  const SELECTED_ACCOUNT_CACHE_KEY = "admin_app_selected_account";

  const [accounts, setAccounts] = useState(() => {
    try {
      const cached = sessionStorage.getItem(ACCOUNT_CACHE_KEY);
      return cached ? JSON.parse(cached) : [];
    } catch (error) {
      console.warn("Failed to parse cached accounts", error);
      return [];
    }
  });

  const [selectedAccount, setSelectedAccountState] = useState(() => {
    try {
      const cached = sessionStorage.getItem(SELECTED_ACCOUNT_CACHE_KEY);
      return cached ? JSON.parse(cached) : null;
    } catch (error) {
      console.warn("Failed to parse cached selected account", error);
      return null;
    }
  });

  const [accountsLoading, setAccountsLoading] = useState(false);
  const [accountsError, setAccountsError] = useState(null);
  const hasFetchedAccountsRef = useRef(false);

  // Create enhanced API call function
  const apiCallWithTokenCheck = createApiCallWithTokenCheck(getIdTokenClaims, logout, loginWithRedirect);

  const setSelectedAccount = useCallback((account) => {
    setSelectedAccountState(account);
    if (account) {
      sessionStorage.setItem(SELECTED_ACCOUNT_CACHE_KEY, JSON.stringify(account));
    } else {
      sessionStorage.removeItem(SELECTED_ACCOUNT_CACHE_KEY);
    }
  }, []);

  const loadAccounts = useCallback(async () => {
    if (accountsLoading || hasFetchedAccountsRef.current) {
      console.log("loadAccounts: skipping", { accountsLoading, hasFetched: hasFetchedAccountsRef.current });
      return;
    }

    try {
      console.log("loadAccounts: starting fetch");
      setAccountsLoading(true);
      setAccountsError(null);
      hasFetchedAccountsRef.current = true;
      
      const tokenClaims = await getIdTokenClaims();
      const idToken = tokenClaims?.__raw;
      if (!idToken) {
        throw new Error("Missing ID token");
      }

      console.log("loadAccounts: fetching from API");
      const response = await fetch(`${config.baseURL}/admin/v1/account`, {
        method: "GET",
        headers: {
          Authorization: `Bearer ${idToken}`,
          "Content-Type": "application/json",
        },
      });

      if (!response.ok) {
        throw new Error(`HTTP error! Status: ${response.status}`);
      }

      const result = await response.json();
      console.log("loadAccounts: received result", result);
      const normalized = Array.isArray(result)
        ? result.map((account) => ({
            account_id: account.account_id,
            name: account.name,
            owner_user_id: account.owner_user_id,
            ...account,
          }))
        : [];

      console.log("loadAccounts: normalized accounts", normalized);
      setAccounts(normalized);
      sessionStorage.setItem(ACCOUNT_CACHE_KEY, JSON.stringify(normalized));

      if (selectedAccount) {
        const exists = normalized.some(
          (account) => account.account_id === selectedAccount.account_id
        );
        if (!exists) {
          setSelectedAccount(null);
        }
      }
    } catch (error) {
      console.error("Error fetching accounts:", error);
      setAccountsError(error.message);
      hasFetchedAccountsRef.current = false;
    } finally {
      setAccountsLoading(false);
    }
  }, [accountsLoading, getIdTokenClaims, selectedAccount, setSelectedAccount]);

  // Function to check and renew token
  const checkAndRenewToken = useCallback(async () => {
    if (!isLoading && !isAuthenticated) {
      // User is not authenticated, redirect to login
      clearLocalStorageItems();
      localStorage.setItem('redirectPath', window.location.pathname);
      loginWithRedirect();
      return;
    }
    if (!isLoading) {
      console.log("Checking and renewing token");
      try {
          const checkToken = await getIdTokenClaims();
          // console.log(checkToken);
          if (checkToken) {
            // Log current token status
            logTokenStatus(checkToken);
    
            // Check if the token has expired
            if (isTokenExpired(checkToken)) {
              console.log("ID Token has expired - logging out user");
              // Handle the expiration scenario (e.g., log out, renew token)
              clearLocalStorageItems();
              logout({ logoutParams: { returnTo: window.location.origin } });
            }
          }
        
      } catch (error) {
        clearLocalStorageItems();
        localStorage.setItem('redirectPath', window.location.pathname);
        loginWithRedirect();
        console.error("Error renewing token: ", error);
        // Log the user out if token renewal fails
        
      }
    }
  }, [
    isAuthenticated,
    isLoading,
    getIdTokenClaims,
    loginWithRedirect,
    logout,
  ]);


  // Set up an interval for the token check
  useEffect(() => {
    checkAndRenewToken(); // Check immediately
    
    const intervalId = setInterval(checkAndRenewToken, 15 * 60 * 1000); // Check every 15 minutes

    // Clean up interval on unmount
    return () => clearInterval(intervalId);
  }, [checkAndRenewToken]);

  useEffect(() => {
    console.log("Context: useEffect for loadAccounts", {
      user: !!user,
      accountsLoading,
      hasFetched: hasFetchedAccountsRef.current,
    });
    if (user && !accountsLoading && !hasFetchedAccountsRef.current) {
      console.log("Context: triggering loadAccounts");
      loadAccounts();
    }
  }, [user, accountsLoading, loadAccounts]);

  useEffect(() => {
    if (!isAuthenticated) {
      setAccounts([]);
      sessionStorage.removeItem(ACCOUNT_CACHE_KEY);
      setSelectedAccount(null);
      hasFetchedAccountsRef.current = false;
    }
  }, [isAuthenticated, setSelectedAccount]);

  return (
    <AppContext.Provider
      value={{
        isOpen,
        setIsOpen,
        isLoad,
        setIsLoad,
        data,
        setData,
        filter,
        setFilter,
        channelData,
        setChannelData,
        selectedChannel,
        setSelectedChannel,
        selectedType,
        setSelectedType,
        currentPage,
        setCurrentPage,
        apiCallWithTokenCheck,
        checkAndRenewToken,
        accounts,
        accountsLoading,
        accountsError,
        loadAccounts,
        selectedAccount,
        setSelectedAccount,
      }}
    >
      {children}
    </AppContext.Provider>
  );
}
